import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send, Bot } from 'lucide-react';
import { API } from '../api';

// Rule-based FAQ bot for Travelsera
const QUICK_REPLIES = [
  'How do I book a package?',
  'What is the cancellation policy?',
  'Which destinations do you cover?',
  'Do you offer international tours?',
  'How can I contact you?',
];

const KNOWLEDGE = [
  {
    keys: ['hello', 'hi', 'hey', 'hola', 'namaste', 'good morning', 'good evening'],
    reply:
      "Hey there! \ud83d\udc4b Welcome to Travelsera. I'm your travel assistant. Ask me anything about our packages, destinations, prices or booking process!",
  },
  {
    keys: ['book', 'booking', 'reserve', 'reservation', 'how do i book'],
    reply:
      "Booking is easy! Pick a package from our Bestsellers or India Tours, click 'View Details', and use the enquiry form on the right. Our travel expert will call you within 24 hours to confirm dates and process the booking.",
  },
  {
    keys: ['cancel', 'cancellation', 'refund'],
    reply:
      'Cancellation charges depend on how close to the departure date you cancel: 30+ days before = 10% deduction, 15\u201329 days = 25%, 7\u201314 days = 50%, less than 7 days = 100%. Full refunds are given only in exceptional cases like natural calamities.',
  },
  {
    keys: ['destination', 'where', 'places', 'cover'],
    reply:
      'We cover 500+ destinations! In India: Sikkim, Darjeeling, Kashmir, Goa, Rajasthan, Kerala, Andaman, Ladakh, Spiti and more. International: Thailand, Vietnam, Bali, Singapore, Malaysia, Maldives, Bhutan, UAE and beyond.',
  },
  {
    keys: ['international', 'abroad', 'foreign', 'overseas'],
    reply:
      'Yes! We have curated international packages to Thailand, Bali, Vietnam, Singapore, Maldives, Bhutan, UAE and Malaysia. Prices start from INR 15,000/adult. Check our Bestsellers > International Tours tab.',
  },
  {
    keys: ['india', 'domestic', 'indian'],
    reply:
      "Absolutely! Our India Tour Packages include Sikkim-Darjeeling, Ladakh, Kashmir, Spiti Valley, Vaishno Devi, Goa and more. Bharat ke rang, Travelsera ke sang! \ud83c\uddee\ud83c\uddf3",
  },
  {
    keys: ['price', 'cost', 'budget', 'cheap', 'expensive', 'inr', 'rupees'],
    reply:
      'Prices depend on destination, duration and hotel category. Domestic packages start at INR 12,000/adult, international from INR 15,000/adult. Every package page shows the exact starting price with what is included.',
  },
  {
    keys: ['contact', 'call', 'phone', 'reach', 'talk'],
    reply:
      "You can reach us anytime: \ud83d\udcde +91-9650336344, \u2709\ufe0f info@travelsera.com. Or click the WhatsApp bubble on the right for instant chat with a human agent.",
  },
  {
    keys: ['visa', 'passport'],
    reply:
      'We help you with visa documentation guidance for international packages, but visa fees and processing are not included in the package price. We recommend applying at least 3\u20134 weeks before departure.',
  },
  {
    keys: ['flight', 'airfare', 'ticket'],
    reply:
      "Most packages don't include flights unless specifically mentioned. We can book flights for you separately at competitive rates \u2014 just mention it in your enquiry.",
  },
  {
    keys: ['hotel', 'accommodation', 'stay', 'room'],
    reply:
      'All our packages include accommodation in handpicked 3\u20135 star hotels. You can also upgrade to premium or luxury hotels for an additional cost. Every package page lists the exact hotels.',
  },
  {
    keys: ['food', 'meal', 'breakfast', 'dinner'],
    reply:
      'Daily breakfast is included in all packages. Lunch and dinner are usually not included unless specified (e.g., some group tours include a welcome dinner or cruise dinner).',
  },
  {
    keys: ['honeymoon'],
    reply:
      'Congratulations! \ud83d\udc95 Our top honeymoon picks: Bali \u2013 Nusa Penida, Maldives Water Villas, Andaman Islands, Kashmir houseboats. Each includes couple-friendly stays and romantic experiences. Explore the Honeymoon theme on our home page!',
  },
  {
    keys: ['family', 'kids', 'children'],
    reply:
      'Family trips are our specialty! Sikkim-Darjeeling, Kashmir, Kerala and Singapore-Malaysia twin city are super family-friendly with kid-safe activities and family rooms.',
  },
  {
    keys: ['adventure', 'trek', 'hiking'],
    reply:
      'Adventure lovers, we have got you! Ladakh, Spiti Valley, Sikkim and Kashmir offer trekking, river rafting, biking and high-altitude expeditions with certified guides.',
  },
  {
    keys: ['payment', 'pay', 'credit card', 'upi'],
    reply:
      'We accept UPI, all major credit/debit cards, net banking and bank transfers. Typically 25% advance to confirm booking and rest 15 days before departure.',
  },
  {
    keys: ['thanks', 'thank you', 'thankyou', 'thx'],
    reply: "You're most welcome! \ud83d\ude0a Happy travels with Travelsera. Feel free to ask anything else!",
  },
  {
    keys: ['bye', 'goodbye', 'see you'],
    reply: 'Bye for now! \ud83d\udc4b Come back anytime. Journeys, redefined \u2014 with Travelsera.',
  },
];

const DEFAULT_REPLY =
  "Hmm, I'm not sure about that one. \ud83d\ude45 For detailed help, tap the enquiry button and one of our travel experts will reach out. You can also call us at +91-9650336344.";

const findReply = (text) => {
  const q = text.toLowerCase();
  for (const item of KNOWLEDGE) {
    if (item.keys.some((k) => q.includes(k))) return item.reply;
  }
  return DEFAULT_REPLY;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: "Hi! I'm Sera \ud83e\udded, your Travelsera assistant. How can I help plan your trip today?" },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [remoteFaqs, setRemoteFaqs] = useState(null); // fetched FAQs, or null while loading
  const bottomRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/chat-faqs`).then((res) => {
      if (Array.isArray(res.data) && res.data.length > 0) setRemoteFaqs(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('travelsera:open-chat', handler);
    return () => window.removeEventListener('travelsera:open-chat', handler);
  }, []);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const findReplyLive = (text) => {
    const q = text.toLowerCase();
    if (remoteFaqs && remoteFaqs.length > 0) {
      for (const f of remoteFaqs) {
        if ((f.keywords || []).some((k) => q.includes(String(k).toLowerCase()))) return f.answer;
      }
      return DEFAULT_REPLY;
    }
    return findReply(text);
  };

  const send = (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      const reply = findReplyLive(msg);
      setMessages((m) => [...m, { role: 'bot', text: reply }]);
      setTyping(false);
    }, 700);
  };

  return (
    <>
      {/* Panel */}
      {open && (
        <div className="fixed bottom-16 md:bottom-5 right-3 md:right-5 z-50 w-[360px] max-w-[calc(100vw-1.5rem)] h-[520px] max-h-[75vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200" data-testid="chat-panel">
          {/* Header */}
          <div className="bg-[#0d64a8] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Bot size={18} />
              </div>
              <div>
                <div className="font-semibold text-sm leading-tight">Sera • Travelsera</div>
                <div className="text-[11px] opacity-90 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                  Online • usually replies instantly
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-4 bg-[#f7fafc] space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#0d64a8] text-white rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 120}ms` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          {messages.length <= 2 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 bg-[#f7fafc]">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-[11px] border border-[#0d64a8]/30 text-[#0d64a8] px-2.5 py-1 rounded-full hover:bg-[#e8f2fb]"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-gray-100 p-2 flex items-center gap-2 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 outline-none text-sm px-3 py-2 border border-gray-200 rounded-full focus:border-[#0d64a8]"
            />
            <button type="submit" className="w-9 h-9 rounded-full bg-[#0d64a8] hover:bg-[#0a548e] text-white flex items-center justify-center transition">
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
